// PR-P0-5 reference: UC-5 (Questionnaires — XLSX column mapper) · no ADR · docs/feature-status-and-gaps.md §2.5
// Mockup: .claude/skills/ux-adversary/runs/uc-5-questionnaires/
// Systems Architecture: §5.13
import { defineTemplate, field } from '../egress/data-classes.js';
export const MAPPING_INSTRUCTIONS = [
    'You are helping ingest a compliance questionnaire spreadsheet.',
    'The user gives you the header row and a few sample data rows.',
    'Identify which column carries each of: the question text, the response/answer,',
    'the section or category, and the evidence link/reference.',
    'Return column identifiers as they appear in the headers list. Use null when',
    'no reasonable column exists for a field. Response, section, and evidence',
    'are optional — question is required.',
].join(' ');
/** ADR-0073: reviewed static template; the sheet sample is the classified input. */
export const MAPPING_TEMPLATE = defineTemplate('questionnaire.map_columns', 'v1', MAPPING_INSTRUCTIONS, []);
export const MAPPING_SCHEMA = {
    question_col: 'string (header value)',
    response_col: 'string|null',
    section_col: 'string|null',
    evidence_col: 'string|null',
    confidence: '0..1',
    rationale: 'short string',
};
function normalizeHeader(v, headers) {
    if (typeof v !== 'string' || !v.trim())
        return null;
    const target = v.trim().toLowerCase();
    // Accept exact match (case-insensitive). Reject anything else — LLM
    // fabrications land as null (preparer picks from the modal).
    const found = headers.find((h) => h.trim().toLowerCase() === target);
    return found ?? null;
}
/**
 * LLM mapping call, through the egress gateway (ADR-0073). Returns a proposal
 * the route stores on the questionnaire row (or hands directly to the client).
 * The caller supplies `headers` and `sampleRows` from the parsed XLSX. A
 * refused call (federal mode, no eligible provider) uses the header heuristic;
 * the preparer confirms the mapping either way.
 */
export async function proposeMappingWithLlm(llm, sample) {
    const payload = JSON.stringify({ headers: sample.headers, rows: sample.sampleRows });
    const result = await llm.run({ task: 'questionnaire.map_columns', template: MAPPING_TEMPLATE, input: field('questionnaire.sheet_sample', payload), schemaHint: MAPPING_SCHEMA });
    if (result.status !== 'ok')
        return heuristicProposal(sample);
    const data = result.output;
    const proposal = {
        question_col: normalizeHeader(data.question_col, sample.headers),
        response_col: normalizeHeader(data.response_col, sample.headers),
        section_col: normalizeHeader(data.section_col, sample.headers),
        evidence_col: normalizeHeader(data.evidence_col, sample.headers),
        confidence: typeof data.confidence === 'number' ? Math.max(0, Math.min(1, data.confidence)) : result.confidence,
        rationale: typeof data.rationale === 'string' ? data.rationale : undefined,
    };
    // If the LLM couldn't identify a question column, fall back to the
    // heuristic — the modal renders whatever we surface here as the default,
    // and null defaults are the worst UX.
    if (!proposal.question_col) {
        const h = heuristicProposal(sample);
        return { ...h, confidence: Math.min(h.confidence, proposal.confidence) };
    }
    return proposal;
}
export function heuristicProposal(sample) {
    const findByKeywords = (keywords) => {
        for (const h of sample.headers) {
            const lower = h.trim().toLowerCase();
            if (!lower)
                continue;
            if (keywords.some((k) => lower.includes(k)))
                return h;
        }
        return null;
    };
    const question = findByKeywords(['question', 'query', 'ask', 'requirement', 'control']);
    const response = findByKeywords(['response', 'answer', 'reply']);
    const section = findByKeywords(['section', 'category', 'domain', 'group', 'area']);
    const evidence = findByKeywords(['evidence', 'link', 'reference', 'artifact', 'proof']);
    return {
        question_col: question ?? sample.headers[0] ?? null,
        response_col: response,
        section_col: section,
        evidence_col: evidence,
        confidence: question ? 0.4 : 0.15,
        rationale: 'Heuristic keyword match on header labels.',
    };
}
/**
 * Turn already-parsed XLSX rows into extracted questions using a confirmed
 * mapping. Row indices become `seq_no` (1-indexed); empty question cells
 * drop the row entirely. Response/evidence columns are ignored by the
 * question-extraction stage — they'll be surfaced to the reviewer in the
 * draft UI (M5) as "customer-supplied context" alongside the draft answer.
 */
export function applyMappingToRows(rows, mapping) {
    if (!mapping.question_col)
        return [];
    const out = [];
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const text = (row[mapping.question_col] ?? '').trim();
        if (!text)
            continue;
        const section = mapping.section_col ? (row[mapping.section_col] ?? '').trim() || null : null;
        out.push({
            seq_no: out.length + 1,
            section,
            question_type: 'unknown',
            text,
        });
    }
    return out;
}
//# sourceMappingURL=questionnaire-xlsx-mapper-service.js.map